// Live score overlay drawn OVER the Cloudflare player for the live stream of a scoreboard-tracked game.
// Connects to the public GameHub (/hubs/game — no auth), joins the match room, and renders a compact
// scorebug from LiveScoreUpdated pushes. Mirrors the web Watch page: a ~7s buffer so the score doesn't
// lead the (lagged) live video, and it drops the bug ~30s after the game goes Final / scoring ends.
import { HubConnectionBuilder, type HubConnection } from '@microsoft/signalr';
import type { EmbedConfig } from '../types';
import { h } from '../ui';

// Subset of the API's LiveGameStateDto (serialized camelCase) that the scorebug needs.
interface LiveScore {
  matchId?: string | null;
  homeTeamName?: string | null;
  awayTeamName?: string | null;
  homeScore?: number;
  awayScore?: number;
  currentPeriod?: number;
  gameClockSeconds?: number;
  gamePhase?: string | null;
}

const BUFFER_MS = 7000; // keep the score ~in sync with the lagged live video

function fmtClock(seconds?: number): string {
  if (seconds == null || seconds < 0) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}
const shortName = (name?: string | null): string => (name || '').trim().slice(0, 14) || '—';

/**
 * Attach a live score overlay onto `layer` (a child placed over the player). Best-effort: if the hub
 * can't be reached the player is unaffected. Returns a cleanup function; the overlay also self-disposes
 * once its `layer` is removed from the DOM (e.g. the viewer navigates to another stream).
 */
export function attachScoreOverlay(layer: HTMLElement, cfg: EmbedConfig, matchId: string): () => void {
  layer.innerHTML = '';
  layer.style.cssText =
    'position:absolute; top:10px; left:50%; transform:translateX(-50%); z-index:8; pointer-events:none;' +
    'display:none; font-family:system-ui,-apple-system,sans-serif;';

  const bug = h('div', {
    style:
      'display:flex; align-items:center; gap:8px; padding:6px 12px; border-radius:10px;' +
      'background:rgba(12,12,12,0.82); color:#fff; box-shadow:0 2px 10px rgba(0,0,0,0.4);' +
      'font-size:15px; font-weight:600; letter-spacing:.3px; white-space:nowrap;',
  });
  layer.append(bug);

  let disposed = false;
  // Time-stamped buffer of incoming pushes + a RECURRING promoter that shows each ~7s later (to match the
  // lagged live video). A per-push debounce timer would be perpetually reset by the every-second clock/score
  // updates and never fire — freezing the bug until a reconnect (the bug this replaces).
  const buffer: { at: number; state: LiveScore }[] = [];
  let promoteTimer: ReturnType<typeof setInterval> | undefined;
  let hideTimer: ReturnType<typeof setTimeout> | undefined;

  const conn: HubConnection = new HubConnectionBuilder()
    .withUrl(`${cfg.baseApiUrl}/hubs/game`)
    .withAutomaticReconnect()
    .build();

  const cleanup = (): void => {
    if (disposed) return;
    disposed = true;
    if (promoteTimer) clearInterval(promoteTimer);
    if (hideTimer) clearTimeout(hideTimer);
    try { void conn.invoke('LeaveGameRoom', matchId); } catch { /* ignore */ }
    try { void conn.stop(); } catch { /* ignore */ }
    layer.remove();
  };

  const mk = (txt: string, style?: string): HTMLElement => h('span', style ? { style } : undefined, txt);

  const render = (s: LiveScore): void => {
    if (disposed) return;
    if (!layer.isConnected) { cleanup(); return; } // player was removed — stop
    bug.innerHTML = '';
    bug.append(
      mk(shortName(s.homeTeamName)),
      mk(String(s.homeScore ?? 0), 'font-size:18px; font-weight:800'),
      mk('–', 'opacity:.55'),
      mk(String(s.awayScore ?? 0), 'font-size:18px; font-weight:800'),
      mk(shortName(s.awayTeamName)),
    );
    const meta = [s.currentPeriod ? `P${s.currentPeriod}` : '', fmtClock(s.gameClockSeconds)].filter(Boolean).join(' ');
    if (meta) bug.append(mk(meta, 'opacity:.7; font-weight:500; font-size:12px; margin-left:4px'));
    layer.style.display = '';
  };

  const promote = (): void => {
    if (disposed) return;
    // Show the NEWEST state already aged past the buffer delay (drop older ones), so the bug advances
    // continuously ~7s behind the scoreboard — in sync with the lagged video — even under 1/sec updates.
    const cutoff = Date.now() - BUFFER_MS;
    let toShow: LiveScore | null = null;
    while (buffer.length && buffer[0].at <= cutoff) toShow = (buffer.shift() as { at: number; state: LiveScore }).state;
    if (toShow) render(toShow);
  };

  conn.on('LiveScoreUpdated', (state: LiveScore) => {
    if (disposed) return;
    buffer.push({ at: Date.now(), state });
    if (buffer.length > 200) buffer.shift();
    if (!promoteTimer) promoteTimer = setInterval(promote, 500);
    if ((state.gamePhase || '').toLowerCase().includes('final')) {
      if (hideTimer) clearTimeout(hideTimer);
      hideTimer = setTimeout(() => { if (!disposed) layer.style.display = 'none'; }, BUFFER_MS + 30000);
    }
  });
  conn.on('LiveScoringEnded', () => {
    if (hideTimer) clearTimeout(hideTimer);
    hideTimer = setTimeout(() => { if (!disposed) layer.style.display = 'none'; }, 30000);
  });

  void (async () => {
    try {
      await conn.start();
      await conn.invoke('JoinGameRoom', matchId);
    } catch { /* best-effort — player still works without the overlay */ }
    // JoinGameRoom does NOT replay the current score, so without this the bug stays blank until the next
    // point is scored. Fetch the current state (public endpoint) and paint it right away; SignalR pushes
    // then keep it live. Skip if a live push already rendered (it's fresher than this fetch).
    try {
      const res = await fetch(`${cfg.baseApiUrl}/api/live-scoreboard/${matchId}/state`);
      if (res.ok) {
        const state = (await res.json()) as LiveScore;
        if (!disposed && bug.innerHTML === '' && !(state.gamePhase || '').toLowerCase().includes('final')) render(state);
      }
    } catch { /* no initial state — the first live update will fill it in */ }
  })();

  return cleanup;
}
